/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ReactNode } from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

export interface PanelBodyProps {
	/**
	 * The content of the panel body.
	 */
	children: ReactNode;
	/**
	 * Controls the padding of the panel body.
	 */
	spacing?: 'none' | 'default';
	/**
	 * A unique string that appears as data attribute `data-testid` in the rendered code, serving as a hook for automated tests.
	 */
	testId?: string;
}

const styles = cssMap({
	body: {
		flexGrow: 1,
		overflow: 'auto',
		overflowWrap: 'anywhere',
	},
	defaultSpacing: {
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
		paddingBlockStart: token('space.250'),
		paddingBlockEnd: token('space.0'),
	},
});

/**
 * The PanelBody component provides the main content area for panels
 * with consistent styling and scroll behavior.
 */
export function PanelBody({ children, spacing = 'default', testId }: PanelBodyProps): JSX.Element {
	return (
		<Box testId={testId} xcss={cx(styles.body, spacing === 'default' && styles.defaultSpacing)}>
			{children}
		</Box>
	);
}
